import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
// Deep imports, not the package barrels — this component is mounted by AppShell,
// so it is on every page's boot path. `@attendance/ui`'s barrel drags Dialog's
// CJS focus-trap.
import { cn } from '@attendance/ui/utils';
import { insetFocusRing } from '@attendance/ui/focus';
import { useGlobalSearch } from './useGlobalSearch.js';
import { SearchResults } from './SearchResults.js';
import { useCommandPaletteOpen } from './useCommandPaletteOpen.js';
import { ICON } from '@attendance/ui';

/**
 * Command palette — global ⌘K / Ctrl+K.
 *
 * It searches TWO things at once: the nav destinations the current role can
 * reach (as it always has), and the DATA behind them — people, worksites,
 * departments, designations, labour suppliers — via `GET /api/search`. Before
 * this, finding a record meant already knowing which screen listed it.
 *
 * Three response states are kept distinct on purpose, because this codebase has
 * repeatedly been bitten by conflating them:
 *   - LOADING — a request is in flight; the list is not yet an answer
 *   - EMPTY   — the server answered, and there is genuinely nothing
 *   - ERROR   — the search FAILED. This must never render as "no results";
 *               a failed request that looks like an empty one tells the reader
 *               the record does not exist when it may well.
 * The pages half keeps working in every one of them — a failed data search
 * degrades the palette to what it used to be, rather than breaking it.
 *
 * Accessible: role=dialog, scrim dismiss, focus moves to the input, the results
 * are an ARIA listbox driven by `aria-activedescendant` so ↑/↓/Enter/Esc work
 * across the combined list while focus stays in the text box.
 */
export function CommandPalette(): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useCommandPaletteOpen(); // Ctrl/⌘+K, Esc, the header button
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const search = useGlobalSearch(query, open);
  const { flat } = search;
  const activeIndex = flat.length === 0 ? -1 : Math.min(active, flat.length - 1);
  const optionId = (index: number): string => `command-option-${index}`;

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => setActive(0), [query]);

  // Keep the highlighted row visible as ↑/↓ walks past the fold.
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = listRef.current?.querySelector(`#${CSS.escape(optionId(activeIndex))}`);
    // Feature-detected: jsdom has no scrollIntoView, and scrolling is a
    // nicety — it must never be able to take the palette down with it.
    if (el instanceof HTMLElement && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!open) return null;

  function go(to: string): void {
    setOpen(false);
    navigate(to);
  }

  function onInputKey(e: ReactKeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = flat[activeIndex];
      if (row) go(row.to);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Tab') {
      // `aria-modal`, and the input is the palette's only tab stop (the options
      // are driven by `aria-activedescendant`), so Tab has nowhere inside to go.
      // Left alone it walked out onto the page BEHIND the scrim — focus on a
      // control the reader cannot see, with the palette still open over it.
      e.preventDefault();
    }
  }


  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[10vh]"
      role="dialog"
      aria-modal="true"
      aria-label={t('command.searchTitle', 'Search')}
    >
      <button
        type="button"
        className="absolute inset-0 bg-scrim/50"
        aria-hidden
        onClick={() => setOpen(false)}
        tabIndex={-1}
      />
      {/* `shadow-popover`, not the deprecated `shadow-elev-2xl` alias it used to
          take. The palette is the popover ROLE by definition — it floats over
          the page with nothing behind it but a scrim — and the popover shadow is
          the system's only two-layer one for exactly that reason: a wide soft
          pool places it above the page and a tight contact edge keeps its own
          boundary readable against the warm paper, which a single layer cannot
          do at once. Same rendered value as the alias (`--shadow-2xl` already
          resolved to `--shadow-popover`), so this names the role rather than
          changing the look. */}
      <div className="relative w-full max-w-dialog overflow-hidden rounded-modal border border-border bg-surface shadow-popover">
        <div className="flex items-center gap-element border-b border-border px-3">
          <Search size={ICON.control} className="shrink-0 text-muted-foreground" aria-hidden />
          {/* Focus: the INSET variant of the canon, not the outset one. This
              input is `h-12` in a header row flush with the top edge of an
              `overflow-hidden` dialog, so an outline drawn 2px OUTSIDE it is
              clipped away and the field is back to having no indicator at all
              — which is what it had, a bare `focus:outline-none` with no
              replacement and no `focus-within` on the wrapper. */}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder={t('command.searchPlaceholder', 'Search pages, people and records…')}
            aria-label={t('command.searchPlaceholder', 'Search pages, people and records…')}
            role="combobox"
            aria-expanded
            aria-controls="command-listbox"
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
            className={cn(
              'h-12 w-full bg-transparent text-copy text-foreground placeholder:text-muted-foreground',
              insetFocusRing
            )}
          />
          {search.searching && (
            <Loader2
              size={ICON.control}
              className="shrink-0 animate-spin text-muted-foreground"
              aria-hidden
            />
          )}
        </div>
        <SearchResults
          search={search}
          listId="command-listbox"
          optionId={optionId}
          activeIndex={activeIndex}
          setActive={setActive}
          onPick={go}
          listRef={listRef}
        />
      </div>
    </div>
  );
}
